import React from "react";
import { Boxes, Cpu, Users } from "lucide-react";
import { num, usd } from "./networkFmt";
import { ScopeChip } from "./NetworkHeadline";

function Card({ title, icon, rows, testId, nameOf, onPick, hint }) {
    const max = Math.max(...rows.map((r) => r.total_cost_usd), 0) || 1;
    return (
        <div className="border border-white/10 bg-[#0a0a0a] rounded-md flex flex-col" data-testid={testId}>
            <div className="px-4 py-3 border-b border-white/10 flex items-center gap-2">
                {icon}<div className="font-display text-sm">{title}</div>
                <div className="text-[10px] text-white/40 ml-auto">{rows.length}</div>
            </div>
            <div className="max-h-80 overflow-y-auto flex-1">
                {rows.length === 0 ? <div className="p-6 text-center text-xs text-white/40">No spend in range.</div> : rows.slice(0, 50).map((r, i) => (
                    <button key={`${r.key}-${i}`} disabled={!onPick} onClick={() => onPick && onPick(r)} data-testid={`${testId}-row-${i}`}
                        className="w-full text-left px-4 py-2.5 border-b border-white/5 transition-colors duration-150 enabled:hover:bg-white/[0.04]">
                        <div className="flex items-center justify-between gap-3 text-xs">
                            <span className="truncate">{nameOf(r)}</span>
                            <span className="font-mono text-white">{usd(r.total_cost_usd)}</span>
                        </div>
                        <div className="h-0.5 bg-white/5 mt-1.5 rounded"><div className="h-0.5 bg-[#7C3AED]/70 rounded" style={{ width: `${(r.total_cost_usd / max) * 100}%` }} /></div>
                        <div className="flex justify-between mt-1 font-mono text-[10px] text-white/40">
                            <span>{num(r.calls)} calls{r.reuse_charges ? ` · ${num(r.reuse_charges)} reuse` : ""}</span>
                            <span>{usd(r.ai_cost_usd)} AI{r.reuse_cost_usd ? ` + ${usd(r.reuse_cost_usd)} reuse` : ""}</span>
                        </div>
                    </button>
                ))}
            </div>
            {hint && <div className="px-4 py-2 text-[10px] text-white/30 border-t border-white/5">{hint}</div>}
        </div>
    );
}

/** By client (names as reported by their own server) · by feature · by AI model — for the current selection. */
export default function NetworkBreakdowns({ data, onClient, onFeature }) {
    return (
        <div className="space-y-2">
            <ScopeChip data={data} testId="network-breakdown-scope" />
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
                <Card title="By client" testId="network-by-client" icon={<Users className="w-3.5 h-3.5 text-[#7C3AED]" />} rows={data.by_client}
                    nameOf={(r) => r.name} onPick={(r) => onClient(r.key || "-")} hint="Click a client to filter every figure." />
                <Card title="By feature" testId="network-by-feature" icon={<Boxes className="w-3.5 h-3.5 text-cyan-400" />} rows={data.by_feature}
                    nameOf={(r) => r.feature} onPick={(r) => onFeature(r.key)} hint="Click a feature to list its calls below." />
                <Card title="By AI model" testId="network-by-model" icon={<Cpu className="w-3.5 h-3.5 text-emerald-400" />} rows={data.by_model}
                    nameOf={(r) => r.model} hint="AI spend only — reuse charges have no model." />
            </div>
        </div>
    );
}
